import React, { useState } from 'react';
import { SproutIcon, DropletIcon, SparklesIcon, ChevronRightIcon, CheckIcon } from 'lucide-react';
interface ProgramScreenProps {
  openModal: (id: string) => void;
}
export function ProgramScreen({
  openModal
}: ProgramScreenProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    phase1: true,
    phase2: false,
    phase3: false
  });
  const togglePhase = (id: string) => {
    setExpanded(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  return <div className="flex flex-col min-h-screen pb-24 animate-fade-in" data-id="element-201">
      <div className="bg-gradient-to-br from-[#1A3A3A] via-[#2C5A5A] to-[#5BA4A4] px-5 pt-6 pb-8" data-id="element-202">
        <div className="text-white/70 text-[13px] mb-1" data-id="element-203">The Complete Plan</div>
        <div className="text-white text-2xl font-extrabold mb-1.5" data-id="element-204">
          7-Day Reset
        </div>
        <div className="text-white/70 text-sm" data-id="element-205">
          3 phases · 7 days · Total transformation
        </div>
      </div>

      <div className="px-5 py-4 flex flex-col gap-4" data-id="element-206">
        {/* Phase 1 */}
        <div className="bg-white rounded-[20px] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)] cursor-pointer" data-id="element-207">
          <div className="p-[18px] px-5 flex items-center gap-3.5" onClick={() => togglePhase('phase1')} data-id="element-208">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E8F4EA] to-[#C8E6CC] flex items-center justify-center shrink-0" data-id="element-209">
              <SproutIcon className="w-6 h-6 text-sage-dark" data-id="element-210" />
            </div>
            <div className="flex-1" data-id="element-211">
              <div className="text-[11px] text-text-light uppercase tracking-widest font-semibold" data-id="element-212">
                Phase 1
              </div>
              <div className="text-base font-bold text-text-dark" data-id="element-213">
                Preparation & Opening
              </div>
              <div className="text-xs text-sage-dark font-semibold" data-id="element-214">
                Days 1–2
              </div>
            </div>
            <ChevronRightIcon className={`w-5 h-5 text-text-light transition-transform duration-300 ${expanded.phase1 ? 'rotate-90' : ''}`} data-id="element-215" />
          </div>

          {expanded.phase1 && <div className="px-5 pb-5 border-t border-black/5 animate-fade-in" data-id="element-216">
              <p className="text-sm text-text-mid leading-relaxed mb-3.5 pt-3.5" data-id="element-217">
                Focus on hydration with minerals and opening the colon. You
                cannot detox if you are constipated. We use a specific
                Magnesium-Potassium ratio to flush the pipes gently.
              </p>
              <div className="flex items-start gap-3 py-3.5 border-b border-black/5" data-id="element-218">
                <div className="w-9 h-9 rounded-xl bg-[#E8F4EA] text-sage-dark flex items-center justify-center text-sm font-bold shrink-0" data-id="element-219">
                  <CheckIcon className="w-4 h-4" data-id="element-220" />
                </div>
                <div className="flex-1" data-id="element-221">
                  <div className="text-sm font-semibold text-text-dark" data-id="element-222">
                    Day 1 — Opening Ritual
                  </div>
                  <div className="text-xs text-text-light mt-0.5" data-id="element-223">
                    Morning bitter · Mineral hydration · 5-min walk
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 py-3.5" data-id="element-224">
                <div className="w-9 h-9 rounded-xl bg-[#E8F4EA] text-sage-dark flex items-center justify-center text-sm font-bold shrink-0" data-id="element-225">
                  <CheckIcon className="w-4 h-4" data-id="element-226" />
                </div>
                <div className="flex-1" data-id="element-227">
                  <div className="text-sm font-semibold text-text-dark" data-id="element-228">
                    Day 2 — Colon Flush
                  </div>
                  <div className="text-xs text-text-light mt-0.5" data-id="element-229">
                    Cut seed oils · 3-Point Press · Mag drink
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Phase 2 */}
        <div className="bg-white rounded-[20px] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)] cursor-pointer" data-id="element-230">
          <div className="p-[18px] px-5 flex items-center gap-3.5" onClick={() => togglePhase('phase2')} data-id="element-231">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E8F0F4] to-[#C8DEE6] flex items-center justify-center shrink-0" data-id="element-232">
              <DropletIcon className="w-6 h-6 text-blue-700" data-id="element-233" />
            </div>
            <div className="flex-1" data-id="element-234">
              <div className="text-[11px] text-text-light uppercase tracking-widest font-semibold" data-id="element-235">
                Phase 2
              </div>
              <div className="text-base font-bold text-text-dark" data-id="element-236">
                Activation & Drainage
              </div>
              <div className="text-xs text-sage-dark font-semibold" data-id="element-237">
                Days 3–5
              </div>
            </div>
            <ChevronRightIcon className={`w-5 h-5 text-text-light transition-transform duration-300 ${expanded.phase2 ? 'rotate-90' : ''}`} data-id="element-238" />
          </div>

          {expanded.phase2 && <div className="px-5 pb-5 border-t border-black/5 animate-fade-in" data-id="element-239">
              <p className="text-sm text-text-mid leading-relaxed mb-3.5 pt-3.5" data-id="element-240">
                Introduce Drainage Meals — high fiber, high sulfur (to support
                liver Phase II), and low inflammatory fats. This is where the
                "Bloat-Free" feeling becomes permanent.
              </p>
              <div className="flex items-start gap-3 py-3.5 border-b border-black/5" data-id="element-241">
                <div className="w-9 h-9 rounded-xl bg-sage-dark text-white flex items-center justify-center text-sm font-bold shrink-0" data-id="element-242">
                  3
                </div>
                <div className="flex-1" data-id="element-243">
                  <div className="text-sm font-semibold text-text-dark" data-id="element-244">
                    Day 3 — First Drainage Meal
                  </div>
                  <div className="text-xs text-text-light mt-0.5" data-id="element-245">
                    Liver Greens Bowl · Lymph walk · Bitter ritual
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 py-3.5 border-b border-black/5" data-id="element-246">
                <div className="w-9 h-9 rounded-xl bg-cream text-sage-dark flex items-center justify-center text-sm font-bold shrink-0" data-id="element-247">
                  4
                </div>
                <div className="flex-1" data-id="element-248">
                  <div className="text-sm font-semibold text-text-dark" data-id="element-249">
                    Day 4 — Deep Flow
                  </div>
                  <div className="text-xs text-text-light mt-0.5" data-id="element-250">
                    Full Flow Menu · Sulfur plate · Press x2
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 py-3.5" data-id="element-251">
                <div className="w-9 h-9 rounded-xl bg-cream text-sage-dark flex items-center justify-center text-sm font-bold shrink-0" data-id="element-252">
                  5
                </div>
                <div className="flex-1" data-id="element-253">
                  <div className="text-sm font-semibold text-text-dark" data-id="element-254">
                    Day 5 — Bile Boost
                  </div>
                  <div className="text-xs text-text-light mt-0.5" data-id="element-255">
                    Bitter activation · Grab & Go lunch · Evening soak
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Phase 3 */}
        <div className="bg-white rounded-[20px] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)] cursor-pointer" data-id="element-256">
          <div className="p-[18px] px-5 flex items-center gap-3.5" onClick={() => togglePhase('phase3')} data-id="element-257">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FFF4E0] to-[#FFE0A0] flex items-center justify-center shrink-0" data-id="element-258">
              <SparklesIcon className="w-6 h-6 text-yellow-700" data-id="element-259" />
            </div>
            <div className="flex-1" data-id="element-260">
              <div className="text-[11px] text-text-light uppercase tracking-widest font-semibold" data-id="element-261">
                Phase 3
              </div>
              <div className="text-base font-bold text-text-dark" data-id="element-262">
                Deep Refresh & Seal
              </div>
              <div className="text-xs text-sage-dark font-semibold" data-id="element-263">
                Days 6–7
              </div>
            </div>
            <ChevronRightIcon className={`w-5 h-5 text-text-light transition-transform duration-300 ${expanded.phase3 ? 'rotate-90' : ''}`} data-id="element-264" />
          </div>

          {expanded.phase3 && <div className="px-5 pb-5 border-t border-black/5 animate-fade-in" data-id="element-265">
              <p className="text-sm text-text-mid leading-relaxed mb-3.5 pt-3.5" data-id="element-266">
                Dry brushing and infrared heat or hot baths activate the skin —
                your largest emunctory — to release the final heavy load. Wake
                up Day 7 feeling like a different human.
              </p>
              <div className="flex items-start gap-3 py-3.5 border-b border-black/5" data-id="element-267">
                <div className="w-9 h-9 rounded-xl bg-cream text-sage-dark flex items-center justify-center text-sm font-bold shrink-0" data-id="element-268">
                  6
                </div>
                <div className="flex-1" data-id="element-269">
                  <div className="text-sm font-semibold text-text-dark" data-id="element-270">
                    Day 6 — Skin Release
                  </div>
                  <div className="text-xs text-text-light mt-0.5" data-id="element-271">
                    Dry brushing · Infrared/hot bath · Castor wrap
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 py-3.5" data-id="element-272">
                <div className="w-9 h-9 rounded-xl bg-cream text-sage-dark flex items-center justify-center text-sm font-bold shrink-0" data-id="element-273">
                  7
                </div>
                <div className="flex-1" data-id="element-274">
                  <div className="text-sm font-semibold text-text-dark" data-id="element-275">
                    Day 7 — Seal & Celebrate
                  </div>
                  <div className="text-xs text-text-light mt-0.5" data-id="element-276">
                    Full protocol · Maintenance plan · You've done it!
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* 30-Day Extension */}
        <div className="bg-gradient-to-br from-[#2C4A30] to-[#4A7A50] rounded-[20px] p-5 text-white mt-1" data-id="element-277">
          <div className="text-[11px] opacity-70 uppercase tracking-widest mb-1.5" data-id="element-278">
            Bonus
          </div>
          <div className="text-lg font-extrabold mb-1.5" data-id="element-279">
            30-Day Action Plan
          </div>
          <div className="text-sm opacity-80 leading-relaxed mb-3.5" data-id="element-280">
            Weeks 2-4 maintenance protocol to lock in your results and keep the
            gates open for good.
          </div>
          <div onClick={() => openModal('plan30')} className="bg-white/15 rounded-xl py-3 px-4 text-sm font-semibold text-center cursor-pointer border border-white/20 transition-transform active:scale-95" data-id="element-281">
            View Week-by-Week Plan →
          </div>
        </div>
      </div>
    </div>;
}